import { FieldError, Option } from '@spike/model';
import clsx from 'clsx';
import LabelField from 'components/UI/LabelField';
import Select, { SelectOption } from 'components/UI/V3/Forms/Select';
import { useTimeZone } from 'hooks';
import { Moment } from 'moment-timezone';
import { FunctionComponent, useMemo } from 'react';
import {
    getBirthdateWithAgeFallback,
    getEstimatedBirthdateFromAgeValues,
    getPetAgePartsFromBirthdate
} from 'utils/PetAgeUtils';
import styles from './AgeField.module.scss';

interface AgeFieldProps {
    id?: string;
    className?: string;
    birthdate: Moment | undefined;
    age?: Option<string>;
    name?: string;
    errors?: Array<FieldError>;
    disabled?: boolean;
    // eslint-disable-next-line no-unused-vars
    onChange?: (birthdate: Moment | undefined, fieldName?: string) => void;
}

const MAX_YEARS = 26;
const MONTH_OPTIONS: Array<SelectOption<number>> = Array.from({ length: 12 }, (_, value) => ({
    value: `${value}`,
    label: `${value} ${value === 1 ? 'Month' : 'Months'}`,
    data: value
}));
const YEAR_OPTIONS: Array<SelectOption<number>> = Array.from({ length: MAX_YEARS + 1 }, (_, value) => ({
    value: `${value}`,
    label: `${value} ${value === 1 ? 'Year' : 'Years'}`,
    data: value
}));

const AgeField: FunctionComponent<AgeFieldProps> = props => {
    const timezone = useTimeZone();
    const resolvedBirthdate = getBirthdateWithAgeFallback(props.birthdate, props.age?.id, timezone);
    const ageParts = getPetAgePartsFromBirthdate(resolvedBirthdate, timezone);

    const years = useMemo(() => {
        if (!ageParts) return 0;
        return Math.min(ageParts.years, MAX_YEARS);
    }, [ageParts]);

    const months = useMemo(() => {
        if (!ageParts) return 0;
        return ageParts.years > MAX_YEARS ? 11 : ageParts.months;
    }, [ageParts]);

    const updateBirthdate = (nextYears: number, nextMonths: number) => {
        props.onChange &&
            props.onChange(getEstimatedBirthdateFromAgeValues(nextYears, nextMonths, timezone), 'birthdate');
    };

    return (
        <div
            id={props.id}
            className={clsx(styles.root, props.className)}
        >
            <LabelField
                className={styles.label}
                label="Age"
                errors={props.errors}
                name={props.name}
                information="If you enter age, Cuddles estimates the birthdate and keeps age synced automatically."
            />
            <div className={styles.selectors}>
                <Select
                    className={styles.select}
                    options={YEAR_OPTIONS}
                    value={`${years}`}
                    disabled={props.disabled}
                    onChange={value => updateBirthdate(Number(value), months)}
                />
                <Select
                    className={styles.select}
                    options={MONTH_OPTIONS}
                    value={`${months}`}
                    disabled={props.disabled}
                    onChange={value => updateBirthdate(years, Number(value))}
                />
            </div>
        </div>
    );
};

export default AgeField;
